import { ENEMY, ENEMY_VARIANTS, GAME } from '../config';
import type { EnemyVariant } from '../config';
import type { FactionId } from '../../shared/types';

export type { EnemyVariant };

/** Faction IDs in a deterministic order for random selection. */
const ALL_FACTIONS: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

/**
 * Halo colors per faction (0xRRGGBB).
 * celestial=yellow, runic=purple, techno=cyan, elemental=green, dominion=red
 */
export const AFFINITY_HALO_COLORS: Record<FactionId, number> = {
  celestial: 0xffe066,
  runic:     0xce93d8,
  techno:    0x4dd0e1,
  elemental: 0x81c784,
  dominion:  0xef5350,
};

export class Enemy {
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  speedPxPerSec: number;
  displaySize: number;
  variant: EnemyVariant;
  alive: boolean = true;
  frozenUntilMs: number = -Infinity;
  /** The faction this enemy is weak to (matching skill deals x1.5 damage). */
  affinityFaction: FactionId;

  constructor(
    position: { x: number; y: number },
    variant: EnemyVariant = 'normal',
    affinityFaction?: FactionId,
  ) {
    this.x = position.x;
    this.y = position.y;
    this.variant = variant;
    const v = ENEMY_VARIANTS[variant];
    this.hp = Math.round(ENEMY.hp * v.hpMult);
    this.maxHp = this.hp;
    this.speedPxPerSec = ENEMY.speedPxPerSec * v.speedMult;
    this.displaySize = Math.round(ENEMY.size * v.sizeMult);
    this.affinityFaction = affinityFaction ??
      ALL_FACTIONS[Math.floor(Math.random() * ALL_FACTIONS.length)];
  }

  /** Returns the halo color for this enemy's affinity faction. */
  haloColor(): number {
    return AFFINITY_HALO_COLORS[this.affinityFaction];
  }

  /**
   * Draws a faint faction-colored halo using a Phaser Graphics object.
   * cx/cy are screen-space center coordinates; radius defaults to displaySize * 0.7.
   */
  drawHalo(
    graphics: { fillStyle(color: number, alpha: number): void; fillCircle(x: number, y: number, r: number): void },
    cx: number,
    cy: number,
    radius?: number,
  ): void {
    const r = radius ?? this.displaySize * 0.7;
    graphics.fillStyle(this.haloColor(), 0.35);
    graphics.fillCircle(cx, cy, r);
  }

  isFrozen(nowMs: number): boolean {
    return nowMs < this.frozenUntilMs;
  }

  freeze(untilMs: number): void {
    this.frozenUntilMs = Math.max(this.frozenUntilMs, untilMs);
  }

  update(dtSec: number, nowMs: number = 0): void {
    if (!this.alive) return;
    if (this.isFrozen(nowMs)) return;
    this.y += this.speedPxPerSec * dtSec;
    if (this.y > GAME.height) {
      this.alive = false;
    }
  }

  takeDamage(amount: number): void {
    this.hp = Math.max(0, this.hp - amount);
    if (this.hp === 0) this.alive = false;
  }
}
